// CouplesCoachingPage — single CTA card + coming-soon block. Visual preserved
// from original; content sourced from page_content row at slug `system-couples-coaching`.

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/api";
import { ColorBlock, type ColorBlockTint } from "@/components/page";

type Size = "sm" | "md" | "lg";

type CouplesContent = {
  kind: "couples-v1";
  header: { eyebrow?: string; title: string; lede?: string };
  featured: {
    href: string;
    tint: ColorBlockTint;
    size?: Size;
    eyebrow?: string;
    title: string;
    body?: string;
    cta?: string;
  };
  comingSoon: {
    tint: ColorBlockTint;
    size?: Size;
    eyebrow?: string;
    body: string;
  };
};

const SLUG = "system-couples-coaching";

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh -mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50">
      <div className="mx-auto max-w-5xl space-y-7">{children}</div>
    </div>
  );
}

export function CouplesCoachingPage() {
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", SLUG],
    queryFn: async (): Promise<CouplesContent> => {
      const res = await api(`/api/kb/${SLUG}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { content: CouplesContent };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex items-center gap-2 p-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          טוען…
        </div>
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame>
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את העמוד</div>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <header>
        {data.header.eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            {data.header.eyebrow}
          </p>
        )}
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-stone-900">
          {data.header.title}
        </h1>
        {data.header.lede && (
          <p className="mt-3 max-w-xl text-base sm:text-lg text-stone-600 leading-snug">
            {data.header.lede}
          </p>
        )}
      </header>

      <Link to={data.featured.href} className="block group">
        <ColorBlock
          tint={data.featured.tint}
          size={data.featured.size ?? "lg"}
          eyebrow={data.featured.eyebrow}
        >
          <p className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 leading-tight">
            {data.featured.title}
          </p>
          {data.featured.body && (
            <p className="mt-3 text-base text-stone-700/85 leading-relaxed max-w-xl">
              {data.featured.body}
            </p>
          )}
          {data.featured.cta && (
            <p className="mt-5 text-sm font-medium text-stone-900 underline-offset-4 group-hover:underline">
              {data.featured.cta}
            </p>
          )}
        </ColorBlock>
      </Link>

      <ColorBlock
        tint={data.comingSoon.tint}
        size={data.comingSoon.size ?? "md"}
        eyebrow={data.comingSoon.eyebrow}
      >
        <p className="text-base text-stone-700/85 leading-relaxed max-w-xl">
          {data.comingSoon.body}
        </p>
      </ColorBlock>
    </PageFrame>
  );
}
